'use client';
import React from 'react';
import { ProductVariantSelector as SharedProductVariantSelector } from '@launchstore/shared-puck';
import type { ProductVariantSelectorProps } from '@launchstore/shared-puck';
import { useProduct, useVariantSelection } from '@/lib/hooks/useProduct';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import type { ProductData, ProductVariantSelectorVariantLike } from '@launchstore/shared-puck';

const toProductData = (p: any): ProductData | null => {
  if (!p) return null;
  return {
    id: p.id, title: p.title,
    variants: (p.variants || []).map((v: any) => ({
      id: v.id, sku: v.sku, weight: v.weight, length: v.length, width: v.width, height: v.height,
      inventory_quantity: v.inventory_quantity, allow_backorder: v.allow_backorder,
      options: v.options,
    })),
    options: (p.options || []).map((o: any) => ({
      id: o.id, title: o.title,
      values: (o.values || []).map((v: any) => ({ id: v.id, value: v.value, metadata: v.metadata })),
    })),
  };
};

const SharedProductVariantSelectorRender = (SharedProductVariantSelector as any).render;

const ProductVariantSelectorImpl: React.FC<ProductVariantSelectorProps> = (props: any) => {
  const { product } = useProduct() as any;
  const {
    selectedOptions, setSelectedOptions, selectedVariant, setSelectedVariant,
  } = useVariantSelection() as any;

  const projectedVariant: ProductVariantSelectorVariantLike | null = selectedVariant ? {
    id: selectedVariant.id, sku: selectedVariant.sku,
    inventory_quantity: selectedVariant.inventory_quantity,
    options: selectedVariant.options,
  } : null;

  // Card mode — the shared selector is built for the product PAGE. Compact
  // card variant: readable text pills ('buttons' style) instead of colour
  // swatches (which render option values like "White" as invisible
  // white-on-white circles), and no option labels.
  const puckContext = (usePuckContext() as any)?.context;
  const cardOverrides = puckContext?.productCard
    ? { selectorStyle: 'buttons', showLabels: false }
    : {};

  return (
    <SharedProductVariantSelectorRender
      {...(props as any)}
      {...cardOverrides}
      // Cards never auto-select: the button stays "View Options" until the
      // shopper explicitly picks an option (then it becomes a live Add to Cart).
      autoSelect={!puckContext?.productCard}
      product={toProductData(product)}
      selectedOptions={selectedOptions}
      setSelectedOptions={setSelectedOptions}
      selectedVariant={projectedVariant}
      setSelectedVariant={(v) => {
        if (v && setSelectedVariant) {
          const match = (product?.variants || []).find((x: any) => x.id === v.id);
          setSelectedVariant(match || null);
        } else if (setSelectedVariant) {
          setSelectedVariant(null);
        }
      }}
    />
  );
};

export const ProductVariantSelector: any = { ...SharedProductVariantSelector, render: (props: any) => <ProductVariantSelectorImpl {...props} /> };
export default ProductVariantSelector;
